package com.eladfinish.bible.contest.screens.lexicographicalSort.model

import androidx.compose.runtime.Immutable
import com.eladfinish.bible.contest.constants.StringConstants.HTML_LINE_BREAK
import com.eladfinish.bible.contest.data.VerseEntity

/**
 * The lexicographical-sort preview as data rather than HTML, for the platform that has no WebView
 * to show the HTML in (desktop). Built from the same sorted verses as the HTML table, so the two
 * cannot drift apart in content — only in looks.
 *
 * @param place the book (and chapter / "by mikud") the table covers, as shown in its title.
 * @param speakerTitle header of the extra column of Tehilim ("משורר") and Iyov ("דובר");
 *   null for every other book, which has no such column.
 */
@Immutable
data class LexicographicalTable(
    val place: String,
    val verseCount: Int,
    val speakerTitle: String?,
    val rows: List<Row>,
) {

    @Immutable
    sealed interface Row {
        data class Verse(
            val number: Int,
            val reysha: String,
            val seyfa: String,
            val speaker: String?,
            val chapter: String,
            val verse: String,
        ) : Row

        /** The divider that stands for the hidden middle of a long table. */
        data class Teaser(val text: String) : Row
    }
}

/**
 * The fixed texts of the table. `table.html` spells the title and the column headers out itself;
 * `LexicographicalTableLabelsTest` fails when the two stop matching.
 */
object LexicographicalTableLabels {
    const val TITLE_FORMAT = "כל הפסוקים בספר %s בסדר לקסיקוגרפי:"
    const val CHECKBOX_HEADER = "🖊"
    const val CHECKBOX = "□"
    const val NUMBER = "מס'"
    const val REYSHA = "רישא"
    const val SEYFA = "סיפא"
    const val CHAPTER = "פרק"
    const val VERSE = "פסוק"

    fun verseCount(count: Int): String = "$count פסוקים"

    fun hiddenVerses(hidden: Int): String = "ועוד ${groupThousands(hidden)} פסוקים בטבלה המלאה"

    /** Groups digits into thousands with commas, e.g. 2507 -> "2,507". */
    private fun groupThousands(value: Int): String =
        value.toString().reversed().chunked(3).joinToString(",").reversed()
}

fun List<VerseEntity>.toTableRows(startNumber: Int = 1): List<LexicographicalTable.Row.Verse> =
    mapIndexed { index, verseEntity ->
        LexicographicalTable.Row.Verse(
            number = startNumber + index,
            reysha = verseEntity.strReysah.orEmpty().htmlLineBreaksToNewLines(),
            seyfa = verseEntity.strSeyfa.orEmpty().htmlLineBreaksToNewLines(),
            speaker = verseEntity.strExtraData,
            chapter = verseEntity.strChapter,
            verse = verseEntity.strVerse,
        )
    }

// The verse parts carry the line breaks of Sifrey Emet as HTML, the only markup they contain.
private fun String.htmlLineBreaksToNewLines(): String = replace(HTML_LINE_BREAK, "\n")
